import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { Divider, Grid, Icon, MenuItem, Typography } from '@mui/material';
import { Permission } from 'interfaces/permissionOption';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useColorMode } from 'components/ColorModeContext';
import { logout } from 'redux/actions/auth';
import { getAdminNavigationState } from 'utils/adminNavigation';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';
import { SupportMenuItems } from './SupportMenuItems';

interface Props {
  name?: string | null;
  canViewOrEditSomeGlobalSettings: boolean;
  onClose: () => void;
  showLoggedInUser?: boolean;
  includeSupportItems?: boolean;
}

export const UserMenuItems: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  canViewOrEditSomeGlobalSettings,
  onClose,
  showLoggedInUser = false,
  includeSupportItems = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { mode, toggleColorMode } = useColorMode();
  const { labSearch } = useCurrentLabId();
  const adminNavigationState = getAdminNavigationState(location);

  const { hasSomePermissions, hasPermission } = usePermissions();
  const canManagePermissions = hasSomePermissions([Permission.DefineRolesToLabUsers, Permission.ManageNucleaiUsers]);
  const canViewCopilotUsage = hasPermission(Permission.ViewCopilotTokenUsage);
  const canManageLabs =
    hasPermission(Permission.CreateLab) || hasPermission(Permission.EditLab) || hasPermission(Permission.DeleteLab);

  return (
    <>
      {showLoggedInUser && (
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Grid container direction="column" sx={{ minWidth: 0 }}>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                Signed in as
              </Typography>
            </Grid>
            <Grid item>
              <Typography data-test-class="logged-in-user-name" variant="body2" noWrap>
                {name || 'Unknown user'}
              </Typography>
            </Grid>
          </Grid>
        </MenuItem>
      )}

      {canViewOrEditSomeGlobalSettings && (
        <MenuItem
          onClick={() => {
            navigate({ pathname: '/admin/operations-settings' }, { state: adminNavigationState });
            onClose();
          }}
        >
          <Grid container columnGap={1}>
            <Grid item>
              <Icon fontSize="medium">
                <SettingsIcon />
              </Icon>
            </Grid>
            <Grid item>
              <Typography variant="body1">Global Settings</Typography>
            </Grid>
          </Grid>
        </MenuItem>
      )}

      {canManagePermissions && (
        <MenuItem
          key={'manage-roles-and-permissions'}
          onClick={() => {
            navigate(
              {
                search: labSearch,
                pathname: '/admin/user-credentials',
              },
              { state: adminNavigationState }
            );
            onClose();
          }}
        >
          <Grid container columnGap={1}>
            <Grid item>
              <Icon fontSize="medium">
                <PeopleIcon />
              </Icon>
            </Grid>
            <Grid item>
              <Typography variant="body1">User Management</Typography>
            </Grid>
          </Grid>
        </MenuItem>
      )}

      {canViewCopilotUsage && (
        <MenuItem
          key={'copilot-usage'}
          onClick={() => {
            navigate({ pathname: '/admin/copilot-usage' }, { state: adminNavigationState });
            onClose();
          }}
        >
          <Grid container columnGap={1}>
            <Grid item>
              <Icon fontSize="medium">
                <BarChartRoundedIcon />
              </Icon>
            </Grid>
            <Grid item>
              <Typography variant="body1">Copilot Usage</Typography>
            </Grid>
          </Grid>
        </MenuItem>
      )}

      {canManageLabs && (
        <MenuItem
          key={'manage-labs'}
          onClick={() => {
            navigate({ pathname: '/admin/manage-labs' }, { state: adminNavigationState });
            onClose();
          }}
        >
          <Grid container columnGap={1}>
            <Grid item>
              <Icon fontSize="medium">
                <CorporateFareIcon />
              </Icon>
            </Grid>
            <Grid item>
              <Typography variant="body1">Manage Labs</Typography>
            </Grid>
          </Grid>
        </MenuItem>
      )}

      <MenuItem
        onClick={() => {
          toggleColorMode();
          onClose();
        }}
      >
        <Grid container columnGap={1}>
          <Grid item>
            <Icon fontSize="medium">{mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}</Icon>
          </Grid>
          <Grid item>
            <Typography variant="body1">{mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}</Typography>
          </Grid>
        </Grid>
      </MenuItem>

      {includeSupportItems && (
        <>
          <Divider />
          <SupportMenuItems onClose={onClose} />
        </>
      )}

      <MenuItem
        data-test-class="logout"
        onClick={() => {
          dispatch(logout());
          onClose();
        }}
      >
        <Grid container columnGap={1}>
          <Grid item>
            <Icon fontSize="medium">
              <LogoutRoundedIcon />
            </Icon>
          </Grid>
          <Grid item>
            <Typography variant="body1">Logout</Typography>
          </Grid>
        </Grid>
      </MenuItem>
    </>
  );
};
